import React, { useState } from "react";
import PropTypes from "prop-types";
import styles from "../ArticleCard/index.module.scss";
import { omitText, Typography } from "@kohler-dsm-library/kohler-core";
import { renderRichText, applyLocaleToUrl } from "@/utils/formatters";
import { useRouter } from "next/router";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { getLinkTarget } from "@/utils/helper";

const ArticleCard = ({ articleData = {} }) => {
  const { locale } = useRouter();
  if (!articleData) return null;
  const {
    background,
    heading,
    subHeading,
    image,
    ctas = [],
    showVideo = false,
    videoUrl = "",
    borderLine = true,
    longVideoUrl = "",
    noDescription = false,
    headingColor,
    borderLineColor,
    hideContentOn = "",
  } = articleData;

  const [isHover, setIsHover] = useState(false);
  const imageUrl = image?.fields?.imageUrl;
  const { asset = {} } = image?.fields || {};
  const { file = {} } = asset?.fields || {};
  const { url: assetUrl = "" } = file;
  const ctasArray = Array.isArray(ctas) ? ctas : [];

  const validBackgrounds = ["white", "black"];
  const bg = validBackgrounds.includes(background) ? background : "white";
  const isWhiteBg = bg === "white";
  const isBlackBg = bg === "black";
  const colorMap = {
    Blue: "#92d7e9",
    White: "#ffffff",
    Black: "#000000",
    Yellow: "#f2a054",
  };

  let hideContent = false;

  switch (locale) {
    case "vn":
      hideContent = hideContentOn === "vn" ? true : false;
      break;

    case "en":
      hideContent = hideContentOn === "en" ? true : false;
      break;

    default:
      hideContent = false;
  }
  const contentHideClass = hideContent ? "hidden" : "";

  const normalizedHeadingColor =
    headingColor && headingColor.trim() !== "" ? headingColor : null;

  const normalizedLineColor =
    borderLineColor && borderLineColor.trim() !== "" ? borderLineColor : null;

  let finalHeadingColor;

  if (isBlackBg) {
    if (normalizedHeadingColor && colorMap[normalizedHeadingColor]) {
      finalHeadingColor = colorMap[normalizedHeadingColor];
    } else {
      finalHeadingColor = "#ffffff";
    }
  } else {
    finalHeadingColor = "#000000";
  }
  let finalLineColor;

  if (isBlackBg) {
    if (normalizedLineColor && colorMap[normalizedLineColor]) {
      finalLineColor = colorMap[normalizedLineColor];
    } else {
      finalLineColor = "#ffffff";
    }
  }
  const description = articleData?.description
    ? {
        text: documentToReactComponents(articleData.description, {
          renderNode: {
            [BLOCKS.HEADING_1]: (node, children) => (
              <h1
                style={{
                  color: finalHeadingColor,
                  fontSize: "35px",
                  paddingBottom: "10px",
                }}
              >
                {children}
              </h1>
            ),
            [BLOCKS.HEADING_2]: (node, children) => (
              <h2
                style={{
                  color: finalHeadingColor,
                  fontSize: "30px",
                  paddingBottom: "10px",
                }}
              >
                {children}
              </h2>
            ),
            [BLOCKS.HEADING_3]: (node, children) => (
              <h3
                style={{
                  color: finalHeadingColor,
                  fontSize: "24px",
                  paddingBottom: "10px",
                }}
              >
                {children}
              </h3>
            ),
            [BLOCKS.HEADING_4]: (node, children) => (
              <h4
                style={{
                  color: finalHeadingColor,
                  fontSize: "21px",
                  paddingBottom: "10px",
                }}
              >
                {children}
              </h4>
            ),
            [BLOCKS.HEADING_5]: (node, children) => (
              <h5
                style={{
                  color: finalHeadingColor,
                  fontSize: "18px",
                  paddingBottom: "10px",
                }}
              >
                {children}
              </h5>
            ),
            [BLOCKS.HEADING_6]: (node, children) => (
              <h6
                style={{
                  color: finalHeadingColor,
                  fontSize: "14px",
                  paddingBottom: "10px",
                }}
              >
                {children}
              </h6>
            ),

            [BLOCKS.PARAGRAPH]: (node, children) => (
              <p style={{ whiteSpace: "pre-line" }}>{children}</p>
            ),

            [BLOCKS.UL_LIST]: (node, children) => (
              <ul style={{ marginLeft: "20px", marginTop: "10px" }}>
                {children}
              </ul>
            ),

            [BLOCKS.LIST_ITEM]: (node, children) => (
              <li style={{ marginBottom: "8px" }}>{children}</li>
            ),

            [INLINES.HYPERLINK]: (node, children) => (
              <a
                href={applyLocaleToUrl(node.data.uri, locale)}
                target={node.data.uri.includes("http") ? "_blank" : "_self"}
                className="menu-link"
                onMouseEnter={() => setIsHover(true)}
                onMouseLeave={() => setIsHover(false)}
                style={{
                  color: isHover ? "#23527c" : "#337ab7",
                  textDecoration: "none",
                  fontWeight: "500",
                  fontFamily: "HelveticaRoman, Helvetica, sans-serif",
                  letterSpacing: "normal",
                }}
              >
                {children}
              </a>
            ),

            renderMark: {
              bold: text => <strong>{text}</strong>,
              italic: text => <em>{text}</em>,
            },

            [BLOCKS.OL_LIST]: (node, children) => (
              <ol
                style={{
                  listStyleType: "decimal",
                }}
              >
                {children}
              </ol>
            ),
          },
        }),
      }
    : null;
  const hasImage = Boolean(imageUrl || assetUrl);
  const imageSrc = imageUrl || assetUrl;
  const hasHeading = Boolean(heading);
  const hasSubHeading = Boolean(subHeading);
  const hasDescription = Boolean(description);

  const onlyImageMode =
    hasImage && !hasHeading && !hasSubHeading && !hasDescription;

  return (
    <div className={`${contentHideClass}`}>
      <section
        className={`${styles.articleSection} ${
          isWhiteBg ? styles.whiteBackground : ""
        }`}
      >
        <div
          className={`${styles.articleContainer} ${
            !hasImage ? styles.noImageContainer : ""
          } ${!hasImage && !hasHeading && !hasSubHeading ? styles.onlyRichDescription : ""}`}
        >
          {onlyImageMode && (hasImage || showVideo) && (
            <div
              className={
                showVideo
                  ? `${styles.onlyVideo}`
                  : `${styles.onlyImageContainer}`
              }
            >
              <div
                className={
                  showVideo
                    ? `${styles.onlyVideoWrapper}`
                    : `${styles.fullImageWrapper}`
                }
              >
                {onlyImageMode && !showVideo && (
                  <ImageSection
                    imageAlt={image?.fields?.altText || "Feature Image"}
                    imageSrc={imageSrc}
                    links={image?.fields?.externalUrl}
                    noDescription={noDescription}
                  />
                )}
                {showVideo && (
                  <div className={styles["video-wrapper"]}>
                    <iframe
                      className={styles["iframe-fullVideo"]}
                      title="Article video content"
                      src={videoUrl || longVideoUrl}
                      allowFullScreen
                    />
                  </div>
                )}
              </div>
              {ctasArray.length > 0 && (
                <div className={styles.ctaContainer}>
                  {ctasArray.map((cta, index) => {
                    const { label, url, mediaUrl, ctaType, openerType } =
                      cta?.fields || {};

                    if (!label) return null;

                    if (ctaType === "Button") {
                      return (
                        <li key={index}>
                          <a
                            key={index}
                            href={applyLocaleToUrl(url || mediaUrl, locale)}
                            target={
                              openerType === "New Tab" ? "_blank" : "_self"
                            }
                            className={`${styles.articlePagesTextLink} ${
                              isWhiteBg
                                ? styles.whiteBgButton
                                : styles.blackBgButton
                            }`}
                          >
                            {label}
                          </a>
                        </li>
                      );
                    }
                    if (ctaType === "Text Link") {
                      return (
                        <li key={index}>
                          <a
                            key={index}
                            target={
                              openerType === "New Tab" ? "_blank" : "_self"
                            }
                            href={applyLocaleToUrl(url || mediaUrl, locale)}
                            className={`${styles.articleTextLink} ${
                              isWhiteBg
                                ? styles.textLinkBlackBg
                                : styles.textLinkWhiteBg
                            }`}
                          >
                            {label}
                          </a>
                        </li>
                      );
                    }
                    return null;
                  })}
                </div>
              )}
            </div>
          )}

          {!onlyImageMode && (
            <>
              {(hasImage || showVideo) && (
                <div className={styles.articleImageWrapper}>
                  {hasImage && !showVideo && (
                    <ImageSection
                      imageAlt={image?.fields?.altText || "Feature Image"}
                      imageSrc={imageSrc}
                      links={image?.fields?.externalUrl}
                      noDescription={noDescription}
                    />
                  )}
                  {showVideo && (
                    <iframe
                      className={styles["iframe-video"]}
                      title="Article video content"
                      src={videoUrl || longVideoUrl}
                      allowFullScreen
                    />
                  )}
                </div>
              )}
              <div className={styles.articleContent}>
                {heading && (
                  <h2
                    className={styles.articleHeading}
                    style={{ color: finalHeadingColor }}
                  >
                    {heading}
                  </h2>
                )}
                {subHeading && (
                  <h3
                    className={styles.articleSubHeading}
                    style={{ color: finalHeadingColor }}
                  >
                    {subHeading}
                  </h3>
                )}
                {description && (
                  <Typography
                    component="div"
                    textColor={isWhiteBg ? "#232323" : "#fff"}
                    {...omitText(description)}
                    className={styles.typographyContent}
                  >
                    {description.text}
                  </Typography>
                )}
                {ctasArray.length > 0 && (
                  <div className={styles.ctaContainer}>
                    {ctasArray.map((cta, index) => {
                      const {
                        label,
                        url,
                        mediaUrl,
                        ctaType,
                        openerType = "",
                      } = cta?.fields || {};

                      if (!label) return null;

                      if (ctaType === "Button") {
                        return (
                          <li key={index}>
                            <a
                              key={index}
                              href={applyLocaleToUrl(url || mediaUrl, locale)}
                              target={
                                openerType === "New Tab" ? "_blank" : "_self"
                              }
                              className={`${styles.articlePagesTextLink} ${
                                isWhiteBg
                                  ? styles.whiteBgButton
                                  : styles.blackBgButton
                              }`}
                            >
                              {label}
                            </a>
                          </li>
                        );
                      }
                      if (ctaType === "Text Link") {
                        return (
                          <li key={index}>
                            <a
                              key={index}
                              target={
                                openerType === "New Tab" ? "_blank" : "_self"
                              }
                              href={applyLocaleToUrl(url || mediaUrl, locale)}
                              className={`${styles.articleTextLink} ${
                                isWhiteBg
                                  ? styles.textLinkBlackBg
                                  : styles.textLinkWhiteBg
                              }`}
                            >
                              {label}
                            </a>
                          </li>
                        );
                      }

                      return null;
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className={styles.articleContainer}>
          {borderLine && (
            <div
              className={styles.cardDivider}
              style={
                finalLineColor
                  ? { borderBottom: `1px solid ${finalLineColor}` }
                  : {}
              }
            ></div>
          )}
        </div>
      </section>
    </div>
  );
};

const ImageSection = ({
  links = "",
  imageSrc = "",
  imageAlt = "",
  image = {},
  noDescription = false,
}) => {
  const hasLink = links != null && links !== "";
  return hasLink ? (
    <a href={links} target={getLinkTarget(links)}>
      <img
        src={imageSrc}
        alt={imageAlt}
        className={noDescription ? styles.imageWithNoDesc : styles.fullImage}
      />
    </a>
  ) : (
    <img
      src={imageSrc}
      alt={imageAlt}
      className={noDescription ? styles.imageWithNoDesc : styles.fullImage}
    />
  );
};

ArticleCard.propTypes = {
  articleData: PropTypes.shape({
    background: PropTypes.string,
    heading: PropTypes.string,
    subHeading: PropTypes.string,
    image: PropTypes.object,
    ctas: PropTypes.array,
    showVideo: PropTypes.bool,
    videoUrl: PropTypes.string,
    description: PropTypes.object,
  }),
};

export default ArticleCard;
